import {Approval} from '@/Core/mobx/ParameterStore/Approval';
import {Parameter} from '@/Core/mobx/ParameterStore/Parameter';
import {Grid} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../PreApprovals/CategoryCard';
import {TextBox} from '@/Core/UI';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '@/Core/mobx/ParameterStore/ApprovalXref';
import {useEffect} from 'react';
import RootStore from '@/Core/mobx/RootStore';
import FormattedDatePicker from './FormattedDatePicker';

type CarrierPortalInformationProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    index: number;
    setCategory: (entry: string) => void;
    setCompleted?: (completed: boolean) => void;
    reportMissingField?: (field: string | null) => void;
};

const CarrierPortalInformation = observer(
    ({
        valueChanged,
        parameters,
        setCategory,
        index,
        setCompleted,
        reportMissingField,
    }: CarrierPortalInformationProps) => {
        const required = (value?: string) => (
            <span>
                {value && value} <span style={{color: 'red'}}>*</span>
            </span>
        );
        const {parameterStore} = RootStore.agentPortalStore;

        const status = parameters.applicationStatus?.value;
        const showApprovedFields = status === 'Approved' || status === 'Delivered';
        const showDeliveredFields = status === 'Delivered';
        const showDeclinedFields = status === 'Declined';

        useEffect(() => {
            const baseComplete = parameters.policyNumber?.value !== '' && parameters.applicationStatus?.value !== '';
            const approvedComplete =
                !showApprovedFields ||
                (parameters.approvedRateClass?.value !== '' && parameters.approvedRating?.value !== '');
            const deliveredComplete = !showDeliveredFields || parameters.policyEffectiveDate?.value !== '';
            const declinedComplete =
                !showDeclinedFields ||
                (parameters.declineReason?.value !== '' &&
                    parameters.underwritingReasonDetailed?.value !== '' &&
                    parameters.declineDate?.value !== '');
            setCompleted?.(baseComplete && approvedComplete && deliveredComplete && declinedComplete);

            let missing: string | null = null;
            if (parameters.policyNumber?.value === '') missing = 'Policy Number';
            else if (parameters.applicationStatus?.value === '') missing = 'Application Status';
            else if (showApprovedFields && parameters.approvedRateClass?.value === '') missing = 'Approved Rate Class';
            else if (showApprovedFields && parameters.approvedRating?.value === '') missing = 'Approved Rating';
            else if (showDeliveredFields && parameters.policyEffectiveDate?.value === '')
                missing = 'Policy Effective Date';
            else if (showDeclinedFields && parameters.declineReason?.value === '') missing = 'Decline Reason';
            else if (showDeclinedFields && parameters.underwritingReasonDetailed?.value === '')
                missing = 'Underwriting Reason (Detailed)';
            else if (showDeclinedFields && parameters.declineDate?.value === '') missing = 'Decline Date';
            reportMissingField?.(missing);
        }, [
            setCompleted,
            reportMissingField,
            parameters.policyNumber?.value,
            parameters.applicationStatus?.value,
            showApprovedFields,
            showDeliveredFields,
            showDeclinedFields,
            parameters.approvedRateClass?.value,
            parameters.approvedRating?.value,
            parameters.policyEffectiveDate?.value,
            parameters.declineReason?.value,
            parameters.underwritingReasonDetailed?.value,
            parameters.declineDate?.value,
        ]);

        return (
            <CategoryCard title={'Carrier Portal Information'} setCategory={setCategory} index={index}>
                <Grid item xs={4}>
                    <TextBox
                        id="policyNumber"
                        label={required('Policy Number')}
                        value={parameters.policyNumber?.value}
                        onChange={(value) =>
                            parameterStore.onValueChangedPreApproval(
                                {...parameters.policyNumber, name: 'policyNumber'},
                                value,
                            )
                        }
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'Application Status',
                            name: 'applicationStatus',
                            options: fieldOptions.applicationStatus.options,
                            ...parameters.applicationStatus,
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
                {showApprovedFields && (
                    <>
                        <Grid item xs={4}>
                            <TextBox
                                id="approvedRateClass"
                                label={required('Approved Rate Class')}
                                value={parameters.approvedRateClass?.value}
                                onChange={(value) => valueChanged(parameters.approvedRateClass, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInputElement
                                param={{
                                    label: 'Approved Rating',
                                    name: 'approvedRating',
                                    options: fieldOptions.approvedRating.options,
                                    ...parameters.approvedRating,
                                }}
                                required={required('')}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                    </>
                )}
                {showDeliveredFields && (
                    <FormattedDatePicker
                        param={parameters.policyEffectiveDate}
                        valueChanged={valueChanged}
                        label={required('Policy Effective Date')}
                    />
                )}
                {showDeclinedFields && (
                    <>
                        <Grid item xs={4}>
                            <SelectInputElement
                                param={{
                                    label: 'Decline Reason',
                                    name: 'declineReason',
                                    options: fieldOptions.declineReason.options,
                                    ...parameters.declineReason,
                                }}
                                required={required('')}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextBox
                                id="underwritingReasonDetailed"
                                label={required('Underwriting Reason (Detailed)')}
                                value={parameters.underwritingReasonDetailed?.value}
                                onChange={(value) => valueChanged(parameters.underwritingReasonDetailed, value)}
                            />
                        </Grid>
                        <FormattedDatePicker
                            param={parameters.declineDate}
                            valueChanged={valueChanged}
                            label={required('Decline Date')}
                        />
                    </>
                )}
            </CategoryCard>
        );
    },
);

export default CarrierPortalInformation;
